// Admin model registry: register model endpoints and reference them by name.
//
// Keys are write-only on the server — responses only ever carry `api_key_masked`, and an
// update omits `api_key` entirely to keep the stored value.
import * as config from "../config.js";

const baseUrl = () => `${config.REST_SERVER_URL}/admin/models`;

/**
 * One request, with the HTTP status preserved so callers can distinguish 400 / 404 / 409.
 * @returns {Promise<{ok: boolean, status: number, data: object}>}
 */
const apiRequest = async (method, path = "", body) => {
  let response;

  try {
    response = await fetch(`${baseUrl()}${path}`, {
      method,
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      credentials: "include",
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (error) {
    console.log("MODEL REGISTRY REQUEST ERROR: ", error);
    return {
      ok: false,
      status: 0,
      data: { success: false, message: error?.message || "Request failed." },
    };
  }

  let text = "";
  try {
    text = await response.text();
  } catch (error) {
    return {
      ok: false,
      status: response.status,
      data: { success: false, message: `Could not read the response: ${error?.message}` },
    };
  }

  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch (error) {
    data = {
      success: false,
      message: `Unexpected non-JSON response (HTTP ${response.status}): ${String(text).slice(0, 200)}`,
    };
  }

  return { ok: response.ok, status: response.status, data };
};

const flatten = ({ ok, status, data }) => ({
  ...data,
  success: ok && data?.success !== false,
  status,
});

const encode = (idOrName) => encodeURIComponent(idOrName ?? "");

/**
 * List registered models plus the read-only environment defaults.
 *
 * @param {object} [options]
 * @param {string} [options.clientId] A client public_id / numeric id. When given the API returns
 *   that client's models *and* the globals; with no clientId the admin console gets everything.
 */
export const getModels = async ({ clientId } = {}) => {
  const query = clientId ? `?client_id=${encodeURIComponent(clientId)}` : "";
  const result = flatten(await apiRequest("GET", query));

  return {
    ...result,
    models: Array.isArray(result?.models) ? result.models : [],
    defaults: result?.defaults ?? {},
  };
};

export const createModel = async (payload) => flatten(await apiRequest("POST", "", payload));

export const updateModel = async (idOrName, payload) =>
  flatten(await apiRequest("PUT", `/${encode(idOrName)}`, payload));

export const deleteModel = async (idOrName) =>
  flatten(await apiRequest("DELETE", `/${encode(idOrName)}`));

/** Test a saved model (uses its stored key). */
export const testModel = async (idOrName, prompt) =>
  flatten(await apiRequest("POST", `/${encode(idOrName)}/test`, prompt ? { prompt } : {}));

/** Test an unsaved endpoint configuration — nothing is stored. */
export const testModelConfig = async (payload, prompt) =>
  flatten(
    await apiRequest("POST", "/test", { ...payload, ...(prompt ? { prompt } : {}) })
  );
